// Centralized Session Manager
// Provides a singleton interface to check and cache auth session state with throttling.
// Prevents request storms against /api/auth/me.

import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';
import type { User } from '../types/auth';

export type SessionState = 'idle' | 'checking' | 'authenticated' | 'unauthenticated';

interface SessionResult {
  user: User | null;
  authenticated: boolean;
  ts: number; // timestamp (ms)
}

interface Listeners {
  (result: SessionResult): void;
}

class SessionManager {
  private state: SessionState = 'idle';
  private lastResult: SessionResult | null = null;
  private inFlight: Promise<boolean> | null = null;
  private listeners: Set<Listeners> = new Set();

  // Tunables
  private SUCCESS_TTL_MS = 5 * 60 * 1000; // 5 minutes for positive session
  private FAILURE_COOLDOWN_MS = 3000;      // 3s before retrying after failure
  private MIN_GAP_MS = 1200;               // Avoid back-to-back network checks
  private lastAttempt = 0;
  private dev = import.meta.env.DEV;

  subscribe(fn: Listeners) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  getUser() { return this.lastResult?.user || null; }
  getState() { return this.state; }
  getLastTs() { return this.lastResult?.ts || 0; }

  private notify() {
    if (this.lastResult) {
      for (const l of this.listeners) l(this.lastResult);
    }
  }

  private withinTTL(now: number, force: boolean): boolean {
    if (force || !this.lastResult) return false;
    const age = now - this.lastResult.ts;
    if (this.lastResult.authenticated) {
      return age < this.SUCCESS_TTL_MS; // success cache
    }
    return age < this.FAILURE_COOLDOWN_MS; // shorter cooldown for failures
  }

  async refresh(force = false): Promise<boolean> {
    const now = Date.now();

    if (this.withinTTL(now, force)) {
      if (this.dev) console.debug('[SessionManager] Cache hit (force?', force, ')');
      return this.lastResult!.authenticated;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    if (!force && now - this.lastAttempt < this.MIN_GAP_MS) {
      if (this.dev) console.debug('[SessionManager] Throttled');
      return this.lastResult?.authenticated || false;
    }

    this.lastAttempt = now;
    this.state = 'checking';

    this.inFlight = (async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        const res = await authenticatedFetch(API_URLS.AUTH.CHECK_SESSION, { method: 'GET', signal: controller.signal });
        clearTimeout(timeout);
        if (res.ok) {
          const data = await res.json();
          this.lastResult = { user: data.user, authenticated: true, ts: Date.now() };
          this.state = 'authenticated';
          if (this.dev) console.debug('[SessionManager] Auth OK');
          this.notify();
          return true;
        } else {
          this.lastResult = { user: null, authenticated: false, ts: Date.now() };
          this.state = 'unauthenticated';
          if (this.dev) console.debug('[SessionManager] Auth FAIL status', res.status);
          this.notify();
          return false;
        }
      } catch (e: unknown) {
        this.lastResult = { user: null, authenticated: false, ts: Date.now() };
        this.state = 'unauthenticated';
        if (this.dev) console.debug('[SessionManager] Error', e instanceof Error ? e.message : String(e));
        this.notify();
        return false;
      } finally {
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  // Invalidate local cached session (e.g., on logout)
  invalidate() {
    this.lastResult = { user: null, authenticated: false, ts: Date.now() };
    this.state = 'unauthenticated';
    this.notify();
  }
}

export const sessionManager = new SessionManager();
